import React, { useEffect, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import styled from '@emotion/styled'
import { usePersistedState } from '../../../hooks/usePersistedState'
import { Box, Button, CircularProgress, LinearProgress } from '@mui/material'
import { PictureAsPdfRounded, ImageRounded, FileDownloadRounded } from '@mui/icons-material'
import { capturePng, downloadCanvas } from '../../../utils/pngExport'
import { H3 } from '../../../layouts/TextStyles'
import { useAppSelector } from '../../../app/hooks'
import { ratingApiSlice } from '../../../features/rating/ratingSlice'
import { curatorStudentsApiSlice } from '../../../features/curator/curatorStudents'
import MonthWeekFilter from '../../../components/monthWeekFilter/MonthWeekFilter'
import CustomInput from '../../../components/muiComponents/customInput'
import FilterSelect from '../../../components/filterSelect/FilterSelect'
import RatingTable from './components/RatingTable'
import PracticeTable from './components/PracticeTable'
import ScoreScaleDialog from './components/ScoreScaleDialog'
import StudentDetailDrawer from './components/StudentDetailDrawer'
import { DEFAULT_PAGE_SIZE } from '../../../utils/constants'
import { ui } from '../../../utils/uiTokens'
import { useInfiniteScroll } from '../../../app/hooks/useInfiniteScroll'
import { StudentRatingState } from '../../../features/types'
import { capitalize } from '../../../utils/functions'
import LevelCards from './components/LevelCards'
import RatingPngSheet from './components/RatingPngSheet'
import MentorRatingTable from './components/MentorRatingTable'
import { levelStats } from './ratingLevel'
import { useGroupLabel, useHeadScope } from '../../head/headScope'
import DirectorSubjectFilter from '../../director/DirectorSubjectFilter'

// Карточки уровней, среднее и PNG считаются по всей группе, а не по подгруженным 25
const ALL_SIZE = 1000
export const LAST_REPORT_KEY = 'rating.lastReport'

const RatingPage = () => {
    const { formatMessage, locale } = useIntl()
    const { basePath, readOnly } = useHeadScope()
    const groupLabel = useGroupLabel()
    const { id } = useAppSelector((state) => state.user.userInfo)

    const [tab, setTab] = useState<'rating' | 'mentors' | 'practice'>('rating')
    const [selected, setSelected] = useState<StudentRatingState | null>(null)
    const [drawerOpen, setDrawerOpen] = useState(false)
    const [checkedIds, setCheckedIds] = useState<number[]>([])
    const [monthNumber, setMonthNumber] = usePersistedState<number | undefined>('curator.monthNumber', undefined)
    const [weekNumber, setWeekNumber] = usePersistedState<number | undefined>('curator.weekNumber', undefined)
    const [courseId, setCourseId] = usePersistedState<number | undefined>('curator.courseId', undefined)
    const [groupId, setGroupId] = usePersistedState<number | undefined>('curator.groupId', undefined)
    const [filter, setFilter] = useState('')
    const [search, setSearch] = useState('')
    const [openScale, setOpenScale] = useState(false)
    const [rows, setRows] = useState<StudentRatingState[]>([])
    const [summary, setSummary] = useState({ totalCount: 0, maxPoint: 0, averagePoint: 0 })
    const [loadedPages, setLoadedPages] = useState(0)
    // Ответы приходят не по порядку: применяем только последний запрос
    const requestIdRef = useRef(0)
    const isLoadingRef = useRef(false)
    // Наблюдатель может сработать до того, как новая порция легла в state,
    // и снова попросить ту же страницу: она бы добавилась в список второй раз
    const loadedPagesRef = useRef(0)

    const { data: courseResponse } = curatorStudentsApiSlice.useFetchCuratorCoursesQuery()
    const { data: groups = [] } = curatorStudentsApiSlice.useFetchCuratorGroupsQuery(courseId, {
        skip: !courseId,
    })

    const [fetchRating, { isFetching }] = ratingApiSlice.useLazyGetStudentRatingQuery()
    const { data: allResponse } = ratingApiSlice.useGetStudentRatingQuery({
        groupId: groupId || 0,
        monthNumber: monthNumber || 0,
        weekNumber: weekNumber || 0,
        filter: '',
        page: 0,
        size: ALL_SIZE,
    })
    const allStudents = allResponse?.data || []
    const stats = levelStats(allStudents)

    const pngRef = useRef<HTMLDivElement>(null)
    const [exporting, setExporting] = useState(false)

    const titleOf = (option?: { title?: string; titleKz?: string; titleRu?: string }) =>
        (option && (option[`title${capitalize(locale)}` as 'titleKz'] || option.title)) || ''
    const courseTitle = titleOf((courseResponse?.data || []).find((c: { id: number }) => c.id === courseId))
    const groupTitle = groupId
        ? titleOf(groups.find((g: { id: number }) => g.id === groupId))
        : formatMessage({ id: 'allGroups' })
    const monthLabel = monthNumber ? `${monthNumber} ${formatMessage({ id: 'month' })}` : ''
    const weekLabel = weekNumber ? `${weekNumber} ${formatMessage({ id: 'week' })}` : ''

    const downloadPng = async () => {
        setExporting(true)
        try {
            // даём листу отрисоваться вне экрана
            await new Promise((r) => setTimeout(r, 100))
            if (!pngRef.current) return
            const canvas = await capturePng(pngRef.current)
            const name = [
                'Рейтинг',
                groupTitle.replace(/\s+/g, '-'),
                monthNumber ? `${monthNumber}-ай` : '',
                weekNumber ? `${weekNumber}-апта` : '',
                new Date().toISOString().slice(0, 10),
            ]
                .filter(Boolean)
                .join('_')
            await downloadCanvas(canvas, `${name}.png`)
        } finally {
            setExporting(false)
        }
    }

    const hasMore = rows.length < summary.totalCount

    const loadPage = async (nextPage: number) => {
        if (nextPage > 1 && (isLoadingRef.current || nextPage <= loadedPagesRef.current)) return

        const requestId = ++requestIdRef.current
        isLoadingRef.current = true

        try {
            const response = await fetchRating({
                groupId: groupId || 0,
                monthNumber: monthNumber || 0,
                weekNumber: weekNumber || 0,
                filter: search,
                page: nextPage - 1,
                size: DEFAULT_PAGE_SIZE,
            }).unwrap()

            if (requestId !== requestIdRef.current) return

            setRows((prev) => (nextPage === 1 ? response.data : [...prev, ...response.data]))
            setSummary({
                // Короткая страница означает конец списка: расхождение с totalCount
                // иначе гоняло бы наблюдатель по пустым страницам без остановки
                totalCount: response.data.length < DEFAULT_PAGE_SIZE
                    ? (nextPage - 1) * DEFAULT_PAGE_SIZE + response.data.length
                    : response.totalCount,
                maxPoint: response.maxPoint,
                averagePoint: response.averagePoint,
            })
            setLoadedPages(nextPage)
            loadedPagesRef.current = nextPage
        } catch (error) {
            // Без остановки наблюдатель повторял бы упавший запрос бесконечно
            if (requestId === requestIdRef.current) {
                setSummary((prev) => ({ ...prev, totalCount: (nextPage - 1) * DEFAULT_PAGE_SIZE }))
            }
        } finally {
            if (requestId === requestIdRef.current) isLoadingRef.current = false
        }
    }

    const sentinelRef = useInfiniteScroll(
        () => loadPage(loadedPages + 1),
        hasMore && !isFetching && loadedPages > 0,
    )

    const handlePeriodChange = (month?: number, week?: number) => {
        setMonthNumber(month)
        setWeekNumber(week)
    }

    const openDrawer = (student: StudentRatingState) => {
        setSelected(student)
        setDrawerOpen(true)
    }

    const toggleCheck = (studentId: number) =>
        setCheckedIds((prev) =>
            prev.includes(studentId) ? prev.filter((x) => x !== studentId) : [...prev, studentId],
        )

    const toggleAll = () =>
        setCheckedIds((prev) =>
            prev.length >= rows.length ? [] : rows.map((r) => r.userSubjectEnrollId),
        )

    const openBulk = (mode: 'pdf' | 'png') => {
        const p = new URLSearchParams()
        p.set('ids', checkedIds.join(','))
        p.set('mode', mode)
        if (monthNumber) p.set('monthNumber', String(monthNumber))
        if (weekNumber) p.set('weekNumber', String(weekNumber))
        window.open(`${basePath}/rating-print?${p.toString()}`, '_blank')
    }

    // Вернулись из отчёта: догружаем страницы, пока не найдём ученика, и прокручиваем к нему
    const [highlightId, setHighlightId] = useState<number | null>(() => Number(sessionStorage.getItem(LAST_REPORT_KEY)) || null)
    const scrolledRef = useRef(false)
    useEffect(() => {
        if (!highlightId || scrolledRef.current || tab !== 'rating' || !loadedPages) return
        if (rows.some((r) => r.userSubjectEnrollId === highlightId)) {
            scrolledRef.current = true
            sessionStorage.removeItem(LAST_REPORT_KEY)
            setTimeout(() => document.getElementById(`rating-row-${highlightId}`)?.scrollIntoView({ block: 'center' }), 0)
        } else if (hasMore && !isLoadingRef.current) {
            loadPage(loadedPages + 1)
        } else if (!hasMore) {
            setHighlightId(null)
        }
    }, [rows, loadedPages, highlightId, tab])

    useEffect(() => {
        setRows([])
        setSummary({ totalCount: 0, maxPoint: 0, averagePoint: 0 })
        setLoadedPages(0)
        loadedPagesRef.current = 0
        loadPage(1)
    }, [groupId, monthNumber, weekNumber, search])

    return (
        <Wrapper>
            <Content>
                <Header>
                    <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                            <H3>{formatMessage({ id: 'rating' })}</H3>
                            <Button
                                variant="contained"
                                size="small"
                                disabled={exporting || !allStudents.length}
                                startIcon={exporting ? <CircularProgress size={16} /> : <FileDownloadRounded />}
                                onClick={downloadPng}
                                sx={{ textTransform: 'none', borderRadius: '10px' }}
                            >
                                {formatMessage({ id: 'downloadPng' })}
                            </Button>
                            <Button
                                variant="outlined"
                                size="small"
                                onClick={() => setOpenScale(true)}
                                sx={{ textTransform: 'none', borderRadius: '10px' }}
                            >
                                {formatMessage({ id: 'scoreScale' })}
                            </Button>
                        </Box>
                        <Subtitle>{formatMessage({ id: 'ratingSubtitle' })}</Subtitle>
                    </Box>

                    {stats.rated > 0 && <LevelCards counts={stats.counts} total={stats.rated} />}
                </Header>

                <Tabs>
                    <Tab
                        active={tab === 'rating'}
                        onClick={() => setTab('rating')}
                    >
                        {formatMessage({ id: 'ratingTable' })}
                    </Tab>
                    {readOnly && (
                        <Tab
                            active={tab === 'mentors'}
                            onClick={() => setTab('mentors')}
                        >
                            {formatMessage({ id: 'headByMentors' })}
                        </Tab>
                    )}
                    <Tab
                        active={tab === 'practice'}
                        onClick={() => setTab('practice')}
                    >
                        {formatMessage({ id: 'practiceAndZachet' })}
                    </Tab>
                </Tabs>

                <Filters>
                    <FilterSelect
                        label={formatMessage({ id: 'course' })}
                        placeholder={formatMessage({ id: 'course' })}
                        value={courseId}
                        options={courseResponse?.data || []}
                        onChange={(value) => {
                            setCourseId(Number(value))
                            setGroupId(undefined)
                        }}
                    />

                    <DirectorSubjectFilter withLabel />

                    <FilterSelect
                        label={formatMessage({ id: 'group' })}
                        placeholder={formatMessage({ id: 'allGroups' })}
                        value={groupId}
                        options={groups.map((g) => ({ id: g.id, title: groupLabel(g) }))}
                        disabled={!courseId}
                        onChange={(value) => {
                            setGroupId(Number(value) || undefined)
                        }}
                    />

                    <SearchBox>
                        <CustomInput
                            margin="none"
                            size="small"
                            fullWidth
                            placeholder={formatMessage({ id: 'searchByStudentName' })}
                            value={filter}
                            onChange={(e) => setFilter(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    setSearch(filter)
                                }
                            }}
                        />
                    </SearchBox>
                </Filters>

                <PeriodRow>
                    <MonthWeekFilter
                        courseId={courseId ?? null}
                        courseSubjectId={(groups.find((g) => g.id === groupId) ?? (readOnly ? undefined : groups[0]))?.courseSubjectId}
                        groupId={groupId}
                        monthNumber={monthNumber}
                        weekNumber={weekNumber}
                        onChange={handlePeriodChange}
                    />

                    <Summary>
                        {tab === 'rating' && checkedIds.length > 0 && (
                            <Box sx={{ display: 'flex', gap: 1 }}>
                                <Button
                                    variant="contained"
                                    size="small"
                                    startIcon={<PictureAsPdfRounded />}
                                    onClick={() => openBulk('pdf')}
                                    sx={{ textTransform: 'none', borderRadius: '10px' }}
                                >
                                    PDF ({checkedIds.length})
                                </Button>
                                <Button
                                    variant="outlined"
                                    size="small"
                                    startIcon={<ImageRounded />}
                                    onClick={() => openBulk('png')}
                                    sx={{ textTransform: 'none', borderRadius: '10px' }}
                                >
                                    PNG ({checkedIds.length})
                                </Button>
                            </Box>
                        )}
                        <Pink>
                            {formatMessage({ id: 'studentsCount' }, { count: summary.totalCount })}
                        </Pink>
                    </Summary>
                </PeriodRow>

                {tab === 'rating' && stats.rated > 0 && (
                    <Average>
                        {formatMessage({ id: 'groupAverage' })}: <b>{stats.average}</b> / {stats.max}
                    </Average>
                )}

                {isFetching && loadedPages === 0 ? (
                    <LinearProgress />
                ) : (
                    <>
                        {tab === 'rating' ? (
                            <RatingTable
                                students={rows}
                                monthNumber={monthNumber}
                                weekNumber={weekNumber}
                                groupId={groupId}
                                filter={search}
                                highlightId={highlightId}
                                onRowClick={openDrawer}
                                checkedIds={checkedIds}
                                onToggleCheck={toggleCheck}
                                onToggleAll={toggleAll}
                            />
                        ) : tab === 'mentors' ? (
                            <MentorRatingTable students={allStudents} />
                        ) : (
                            <PracticeTable
                                students={rows}
                                monthNumber={monthNumber}
                                weekNumber={weekNumber}
                                groupId={groupId}
                                filter={search}
                            />
                        )}

                        <Sentinel ref={sentinelRef}>
                            {isFetching && <CircularProgress size={22} sx={{ color: ui.pink }} />}
                        </Sentinel>
                    </>
                )}
            </Content>

            {exporting && (
                <Box sx={{ position: 'absolute', left: -10000, top: 0 }}>
                    <RatingPngSheet
                        ref={pngRef}
                        students={allStudents}
                        subtitle={[courseTitle, groupTitle, monthLabel, weekLabel].filter(Boolean).join(' · ')}
                        average={stats.average}
                        max={stats.max}
                        counts={stats.counts}
                        rated={stats.rated}
                    />
                </Box>
            )}

            <ScoreScaleDialog
                open={openScale}
                handleClose={() => setOpenScale(false)}
            />

            <StudentDetailDrawer
                student={selected}
                open={drawerOpen}
                onClose={() => setDrawerOpen(false)}
                monthNumber={monthNumber}
                weekNumber={weekNumber}
                totalCount={summary.totalCount}
            />
        </Wrapper>
    )
}

export default RatingPage

const Wrapper = styled.div`
    margin: 10px;
    border-radius: 16px;
`

const Content = styled.div`
    background-color: ${ui.card};
    border: 1px solid ${ui.border};
    border-radius: 16px;
    padding: 24px;
    min-height: 80vh;
`

const Sentinel = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    height: 48px;
`

const Header = styled.header`
    display: flex;
    flex-wrap: wrap;
    gap: 16px;
    align-items: flex-start;
    justify-content: space-between;
    margin-bottom: 20px;
`

const Subtitle = styled.div`
    margin-top: 6px;
    max-width: 620px;
    font-size: 13.5px;
    line-height: 1.5;
    color: ${ui.sub};
`

const Tabs = styled.div`
    display: flex;
    gap: 8px;
    margin-bottom: 20px;
`

const Tab = styled.div<{ active: boolean }>`
    padding: 8px 16px;
    border-radius: 12px;
    cursor: pointer;
    font-size: 13.5px;
    font-weight: ${({ active }) => (active ? 600 : 400)};
    color: ${({ active }) => (active ? ui.pink : ui.sub)};
    background: ${({ active }) => (active ? ui.pinkSoft : 'transparent')};
    border: 1px solid ${({ active }) => (active ? 'transparent' : ui.border)};

    &:hover {
        border-color: ${ui.pink};
        color: ${ui.pink};
    }
`

const Filters = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(190px, 1fr));
    gap: 16px;
    margin-bottom: 20px;
`

const SearchBox = styled.div`
    display: flex;
    align-items: flex-end;
    grid-column: span 2;

    @media screen and (max-width: 600px) {
        grid-column: span 1;
    }
`

const PeriodRow = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 16px;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 20px;
`

const Summary = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 20px;
    font-size: 13px;
    color: ${ui.sub};

    b {
        color: ${ui.ink};
    }
`

const Average = styled.div`
    margin-bottom: 12px;
    font-size: 13.5px;
    color: ${ui.sub};

    b {
        font-size: 20px;
        color: ${ui.ink};
        margin-left: 4px;
    }
`

const Pink = styled.span`
    font-weight: 600;
    color: ${ui.pink};
`
